/**
 * Modal for managing redaction reasons.
 * Allows users to create, edit, and delete reasons that appear in redacted output.
 */

import { useState } from "react";
import { X, Plus, Pencil, Trash2, Check, Loader2, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useReasons } from "../hooks/useReasons";
import type { RedactionReason } from "../types";

interface ReasonsManagementModalProps {
  open: boolean;
  onClose: () => void;
}

export function ReasonsManagementModal({
  open,
  onClose,
}: ReasonsManagementModalProps) {
  const { reasons, isLoading, error, createReason, updateReason, deleteReason } = useReasons();
  const [newReasonTitle, setNewReasonTitle] = useState("");
  const [newReasonDescription, setNewReasonDescription] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingDescription, setEditingDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreateReason = async () => {
    if (!newReasonTitle.trim()) return;

    setIsSubmitting(true);
    const result = await createReason(newReasonTitle.trim(), newReasonDescription.trim());
    setIsSubmitting(false);

    if (result) {
      setNewReasonTitle("");
      setNewReasonDescription("");
    }
  };

  const handleStartEdit = (reason: RedactionReason) => {
    setEditingId(reason.id);
    setEditingTitle(reason.title);
    setEditingDescription(reason.description || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingTitle("");
    setEditingDescription("");
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingTitle.trim()) return;

    setIsSubmitting(true);
    const result = await updateReason(editingId, editingTitle.trim(), editingDescription.trim());
    setIsSubmitting(false);

    if (result) {
      setEditingId(null);
      setEditingTitle("");
      setEditingDescription("");
    }
  };

  const handleDeleteReason = async (id: string) => {
    setIsSubmitting(true);
    await deleteReason(id);
    setIsSubmitting(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      action();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] w-[95vw] bg-white p-0 max-h-[85vh] flex flex-col shadow-xl border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Manage Redaction Reasons
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 mt-0.5">
                Create and manage reasons that appear in redacted documents
              </DialogDescription>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Add new reason section */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-[1fr_1.5fr_auto] gap-3">
              <input
                type="text"
                value={newReasonTitle}
                onChange={(e) => setNewReasonTitle(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleCreateReason)}
                placeholder="Title (e.g., RCW 42.56.230)"
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 transition-colors"
                disabled={isSubmitting}
              />
              <input
                type="text"
                value={newReasonDescription}
                onChange={(e) => setNewReasonDescription(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleCreateReason)}
                placeholder="Description (optional)"
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 transition-colors"
                disabled={isSubmitting}
              />
              <button
                onClick={handleCreateReason}
                disabled={!newReasonTitle.trim() || isSubmitting}
                className="px-4 py-2 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium whitespace-nowrap"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Add Reason
              </button>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Table section */}
          <div className="px-6 py-4">
            {/* Loading state */}
            {isLoading && (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                <span className="text-sm">Loading...</span>
              </div>
            )}

            {/* Empty state */}
            {!isLoading && reasons.length === 0 && (
              <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
                <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-1">No reasons created yet</p>
                <p className="text-xs text-gray-400">Add a reason above to get started</p>
              </div>
            )}

            {/* Table */}
            {!isLoading && reasons.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 w-[180px]">
                        Title
                      </th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                        Description
                      </th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 w-[100px]">
                        Created
                      </th>
                      <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 w-[100px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {reasons.map((reason) => (
                      <tr key={reason.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          {editingId === reason.id ? (
                            <input
                              type="text"
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, handleSaveEdit)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
                              autoFocus
                              disabled={isSubmitting}
                            />
                          ) : (
                            <div>
                              <span className="text-sm text-gray-900 font-medium">
                                {reason.title}
                              </span>
                              <code className="ml-2 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono">
                                [{reason.title}]
                              </code>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {editingId === reason.id ? (
                            <input
                              type="text"
                              value={editingDescription}
                              onChange={(e) => setEditingDescription(e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, handleSaveEdit)}
                              placeholder="Add description..."
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
                              disabled={isSubmitting}
                            />
                          ) : (
                            <span className="text-sm text-gray-500">
                              {reason.description || <span className="text-gray-300 italic">No description</span>}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-500">
                            {formatDate(reason.created_at)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {editingId === reason.id ? (
                              <>
                                <button
                                  onClick={handleSaveEdit}
                                  disabled={!editingTitle.trim() || isSubmitting}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                                  title="Save"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  disabled={isSubmitting}
                                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleStartEdit(reason)}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                  title="Edit"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteReason(reason.id)}
                                  disabled={isSubmitting}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && reasons.length > 0 && (
              <p className="text-xs text-gray-400 mt-3">
                {reasons.length} {reasons.length === 1 ? 'reason' : 'reasons'} total
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0 bg-gray-50">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-800 transition-colors font-medium"
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
